import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../store/i18nSlice';
import { resources } from '../utils/i18nConfig';

/**
 * useTranslation Hook
 * 
 * Provides translation functionality throughout the application.
 * Usage: const { t, currentLanguage } = useTranslation();
 * Then use: t('navigation.dashboard') or t('common.save', 'button') for granular control
 * 
 * Supports nested key access with dot notation:
 * - t('common.save')
 * - t('navigation.productionOrders')
 * - t('pages.productionLog')
 * 
 * Optional component type parameter (for granular i18n control):
 * - t('navigation.dashboard', 'menu')   — Will translate if i18nTranslateMenus = true
 * - t('common.save', 'button')          — Will translate if i18nTranslateButtons = true
 * - t('form.name', 'form')              — Will translate if i18nTranslateForms = true
 * - t('error.required', 'error')        — Will translate if i18nTranslateErrors = true
 * 
 * Without component type, uses default behavior (always translates if i18n enabled).
 */
export function useTranslation() {
  const currentLanguage = useSelector(selectCurrentLanguage);
  const i18nEnabled = useSelector(state => state.i18n.i18nEnabled);
  const i18nTranslateMenus = useSelector(state => state.i18n.i18nTranslateMenus);
  const i18nTranslateButtons = useSelector(state => state.i18n.i18nTranslateButtons);
  const i18nTranslateForms = useSelector(state => state.i18n.i18nTranslateForms);
  const i18nTranslateErrors = useSelector(state => state.i18n.i18nTranslateErrors);
  
  // Get the translation object for current language
  const translations = resources[currentLanguage]?.translation || resources['en'].translation;
  const fallbackTranslations = resources['en']?.translation || {};

  const getNestedValue = (obj, keyPath) => {
    const keys = keyPath.split('.');
    let value = obj;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }
    return typeof value === 'string' ? value : undefined;
  };

  /**
   * Translate a key using dot notation for nested values
   * @param {string} key - Translation key (e.g., 'navigation.dashboard')
   * @param {string} componentType - Optional component type: 'menu', 'button', 'form', 'error'
   * @returns {string} Translated string or key if not found (or if that component type is disabled)
   */
  const t = (key, componentType = null) => {
    if (!key || typeof key !== 'string') {
      return '';
    }

    // Check granular translation flags based on component type
    if (componentType) {
      const granularFlags = {
        menu: i18nTranslateMenus,
        button: i18nTranslateButtons,
        form: i18nTranslateForms,
        error: i18nTranslateErrors,
      };

      // If this component type is disabled, use English fallback so UI doesn't show raw keys
      if (granularFlags[componentType] === false) {
        return getNestedValue(fallbackTranslations, key) || key;
      }
    }

    // If i18n is disabled globally, render English labels instead of raw keys.
    if (!i18nEnabled) {
      return getNestedValue(fallbackTranslations, key) || key;
    }

    return getNestedValue(translations, key)
      || getNestedValue(fallbackTranslations, key)
      || key;
  };

  return { t, currentLanguage };
}

export default useTranslation;
